import { useCallback, useRef } from 'react';

import { di } from 'react-magnetic-di';

import type { UploadEndEventPayload } from '@atlaskit/media-picker/types';

import {
	FileStatus,
	type FilesValue,
} from '../../../../../../../../../../common/types/form/form-fields/field-values';
import { FormRendererLifecycleStage } from '../../../../../../../../../../common/types/lifecycle-handlers';
import {
	useFormRendererActions,
	useLifecycleHandlers,
} from '../../../../../../../../../../controllers/form-renderer-store';

export const useOnUploadEnd: (
	currentFilesValue: FilesValue,
	fieldId: string,
	collectionName?: string,
) => (payload: UploadEndEventPayload) => void = (
	currentFilesValue: FilesValue,
	fieldId: string,
	collectionName?: string,
) => {
	di(useFormRendererActions, useLifecycleHandlers);

	const { updateModifiedValue } = useFormRendererActions();
	const lifecycleHandlers = useLifecycleHandlers();

	// Dynamic state that needs to be accessed from onUploadEnd need to be stored in a ref.
	// This is due to how the Media components work. The callbacks are cached, so passing
	// in a new onUploadEnd callback into Media components will not register the
	// updated callback.
	const currentFilesValueRef = useRef<FilesValue>(currentFilesValue);
	currentFilesValueRef.current = currentFilesValue;

	const onUploadEnd = useCallback(
		(payload: UploadEndEventPayload) => {
			updateModifiedValue(fieldId, {
				files: currentFilesValueRef.current.files,
				fileStatus: FileStatus.UPLOADED,
			});

			lifecycleHandlers[FormRendererLifecycleStage.ATTACHMENT_UPLOAD_END]?.({
				fieldId,
				file: {
					creationDate: payload.file.creationDate,
					identifier: {
						collectionName,
						id: payload.file.id,
						mediaItemType: 'file',
						occurrenceKey: payload.file.occurrenceKey,
					},
					name: payload.file.name,
					size: payload.file.size,
					type: payload.file.type,
				},
				stage: FormRendererLifecycleStage.ATTACHMENT_UPLOAD_END,
			});
		},
		[collectionName, currentFilesValueRef, fieldId, lifecycleHandlers, updateModifiedValue],
	);

	return onUploadEnd;
};
